/// <amd-module name="VC/Common/GitRefService" />
import { IVssService, Services, VssService, RestClients, ServiceOptions } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import { GitRef } from "./Contracts/Git";
import { IGitRestClient } from "./RestClient/Git";
import { isBranch } from "./Utils/Ref";

/**
 * Service for working with git refs (branches and tags)
 */
export interface IDeprecatedGitRefService extends IVssService {
    /**
     * Get all the branches of current user, no pagination. It will use cache.
     */
    getMyBranches(repositoryId: string, projectId: string): Promise<GitRef[]>;

    /**
     * Get the first page of branches. It may use cache to return more pages at once.
     * It won't fetch if there's at least one page fetched.
     */
    getPagedBranches(repositoryId: string, projectId: string): Promise<PagedList<GitRef>>;

    /**
     * Get all the branches with another page loaded. It will use cache to return more pages.
     * If cache was cleared since previous call, it will silently start over returning fresh results.
     */
    getMoreBranches(repositoryId: string, projectId: string, continuationToken: string): Promise<PagedList<GitRef>>;

    /**
     * Gets the branches that match the given search text. It may be a partial result (first page).
     * @param continuationToken If provided, results will start where they were left on a previous call.
     */
    searchBranches(repositoryId: string, projectId: string, searchText: string, continuationToken?: string): Promise<PagedList<GitRef>>;

    /**
     * Get the first page of tags. It may use cache to return more pages at once.
     * It won't fetch if there's at least one page fetched.
     */
    getPagedTags(repositoryId: string, projectId: string): Promise<PagedList<GitRef>>;

    /**
     * Get all the tags with another page loaded. It will use cache to return more pages.
     * If cache was cleared since previous call, it will silently start over returning fresh results.
     */
    getMoreTags(repositoryId: string, projectId: string, continuationToken: string): Promise<PagedList<GitRef>>;

    /**
     * Gets the tags that match the given search text. It may be a partial result (first page).
     * @param continuationToken If provided, results will start where they were left on a previous call.
     */
    searchTags(repositoryId: string, projectId: string, searchText: string, continuationToken?: string): Promise<PagedList<GitRef>>;

    /**
     * Forgets all branches so they are retrieved fresh again on next call.
     */
    invalidateBranchesCache(repositoryId: string): void;

    /**
     * Registers a callback to notify when branches change.
     */
    registerBranchesChangedListener(handler: () => void): void;

    /**
     * Unregisters a callback to notify when branches change.
     */
    unregisterBranchesChangedListener(handler: () => void): void;

    /**
     * Forgets all tags so they are retrieved fresh again on next call.
     */
    invalidateTagsCache(repositoryId: string): void;

    /**
     * Registers a callback to notify when tags change.
     */
    registerTagsChangedListener(handler: () => void): void;

    /**
     * Unregisters a callback to notify when tags change.
     */
    unregisterTagsChangedListener(handler: () => void): void;
}

type DictionaryOfRefs = { [repositoryId: string]: Promise<PagedList<GitRef>>};

export class PagedList<T> extends Array<T> {
    constructor(
        values: T[] | number,
        continuationToken?: string,
    ) {
        if (typeof values === "number") {
            // Methods like map() will call the constructor with the standard "new Array()" signature, passing just
            // the desired array length. Handle that case.
            super(values);
        }
        else {
            // Don't use super(...values) as it would overflow stack above 50000 items. Populate one by one instead.
            super();

            for (const value of values) {
                this.push(value);
            }

            this.continuationToken = continuationToken;
        }
    }

    public readonly continuationToken?: string;
}

const noFilterContains = undefined;
const pageSize = 200;

const enum RefArea {
    heads = "heads",
    tags = "tags",
}

export class GitRefService extends VssService implements IDeprecatedGitRefService {
    private myBranchesByRepository: DictionaryOfRefs = {};
    private branchesByRepository: DictionaryOfRefs = {};
    private tagsByRepository: DictionaryOfRefs = {};
    private refsByArea = {
        [RefArea.heads]: this.branchesByRepository,
        [RefArea.tags]: this.tagsByRepository,
    };

    private readonly branchesChangedListeners: Array<() => void> = [];
    private readonly tagsChangedListeners: Array<() => void> = [];

    public getMyBranches(repositoryId: string, projectId: string): Promise<GitRef[]> {
        if (!this.myBranchesByRepository[repositoryId]) {
            this.myBranchesByRepository[repositoryId] = this.getGitClient()
                .getRefs(repositoryId, projectId, undefined, false, false, true)
                .then(filterBranchesOnly);
        }

        return this.myBranchesByRepository[repositoryId];
    }

    public getPagedBranches(repositoryId: string, projectId: string): Promise<PagedList<GitRef>> {
        return this.getPagedRefs(repositoryId, projectId, RefArea.heads);
    }

    public getMoreBranches(repositoryId: string, projectId: string, continuationToken: string): Promise<PagedList<GitRef>> {
        return this.getMoreRefs(repositoryId, projectId, continuationToken, RefArea.heads);
    }

    public searchBranches = (repositoryId: string, projectId: string, searchText: string, continuationToken?: string): Promise<PagedList<GitRef>> =>
        this.searchPagedRefs(repositoryId, projectId, searchText, RefArea.heads, continuationToken);

    public getPagedTags(repositoryId: string, projectId: string): Promise<PagedList<GitRef>> {
        return this.getPagedRefs(repositoryId, projectId, RefArea.tags);
    }

    public getMoreTags(repositoryId: string, projectId: string, continuationToken: string): Promise<PagedList<GitRef>> {
        return this.getMoreRefs(repositoryId, projectId, continuationToken, RefArea.tags);
    }

    public searchTags = (repositoryId: string, projectId: string, searchText: string, continuationToken?: string): Promise<PagedList<GitRef>> =>
        this.searchPagedRefs(repositoryId, projectId, searchText, RefArea.tags, continuationToken);

    public invalidateBranchesCache(repositoryId: string): void {
        delete this.myBranchesByRepository[repositoryId];
        delete this.branchesByRepository[repositoryId];

        for (const listener of this.branchesChangedListeners) {
            listener();
        }
    }

    public registerBranchesChangedListener(handler: () => void): void {
        this.branchesChangedListeners.push(handler);
    }

    public unregisterBranchesChangedListener(handler: () => void): void {
        const index = this.branchesChangedListeners.indexOf(handler);
        if (index >= 0) {
            this.branchesChangedListeners.slice(index, 1);
        }
    }

    public invalidateTagsCache(repositoryId: string): void {
        delete this.tagsByRepository[repositoryId];

        for (const listener of this.tagsChangedListeners) {
            listener();
        }
    }

    public registerTagsChangedListener(handler: () => void): void {
        this.tagsChangedListeners.push(handler);
    }

    public unregisterTagsChangedListener(handler: () => void): void {
        const index = this.tagsChangedListeners.indexOf(handler);
        if (index >= 0) {
            this.tagsChangedListeners.slice(index, 1);
        }
    }

    private async getPagedRefs(
        repositoryId: string,
        projectId: string,
        refArea: RefArea,
    ): Promise<PagedList<GitRef>> {
        let refs = this.getCachedRefs(refArea, repositoryId);
        if (!refs) {
            const client = this.getRefPaginatedClient();
            refs = client.getPaginatedRefs(repositoryId, projectId, refArea, noFilterContains, pageSize);
            this.cacheRefs(refArea, repositoryId, refs);
        }

        return refs;
    }

    private async getMoreRefs(
        repositoryId: string,
        projectId: string,
        continuationToken: string,
        refArea: RefArea,
    ): Promise<PagedList<GitRef>> {
        const previousPagedListPromise = this.getCachedRefs(refArea, repositoryId);
        if (!previousPagedListPromise) {
            return this.getPagedRefs(repositoryId, projectId, refArea);
        }

        const previousPagedList = await previousPagedListPromise;
        if (!previousPagedList.continuationToken || previousPagedList.continuationToken !== continuationToken) {
            return previousPagedList;
        }

        const client = this.getRefPaginatedClient();
        const refs =
            client.getPaginatedRefs(repositoryId, projectId, refArea, noFilterContains, pageSize, previousPagedList.continuationToken)
            .then(pagedList => new PagedList(
                [...previousPagedList, ...pagedList],
                pagedList.continuationToken));

        this.cacheRefs(refArea, repositoryId, refs);

        return refs;
    }

    private async searchPagedRefs(
        repositoryId: string,
        projectId: string,
        searchText: string,
        refArea: RefArea,
        continuationToken?: string,
    ): Promise<PagedList<GitRef>> {
        const refs = this.getCachedRefs(refArea, repositoryId);
        if (refs) {
            const pagedList = await refs;
            if (!pagedList.continuationToken) {
                const filteredRefs = pagedList.filter(ref => ref.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
                return new PagedList(filteredRefs);
            }
        }

        return this.getRefPaginatedClient().getPaginatedRefs(repositoryId, projectId, refArea, searchText, pageSize, continuationToken);
    }

    private getRefPaginatedClient(): IPaginatedGitRefRestClient {
        return this.pageContext.getRestClient<IPaginatedGitRefRestClient>("IPaginatedGitRefRestClient");
    }

    private getGitClient(): IGitRestClient {
        return this.pageContext.getRestClient<IGitRestClient>("IGitRestClient");
    }

    private getCachedRefs(refArea: RefArea, repositoryId: string): Promise<PagedList<GitRef>> {
        return this.refsByArea[refArea][repositoryId];
    }

    private cacheRefs(refArea: RefArea, repositoryId: string, refs: Promise<PagedList<GitRef>>): void {
        this.refsByArea[refArea][repositoryId] = refs;
    }
}

function concatPagedListPromises<T>(a: Promise<PagedList<T>>, b: Promise<PagedList<T>>): Promise<PagedList<T>> {
    return Promise.all([a, b])
        .then(([listA, listB]) => concatPagedLists(listA, listB));
}

function concatPagedLists<T>(a: PagedList<T>, b: PagedList<T>): PagedList<T> {
    return new PagedList(
        [...a, ...b],
        b.continuationToken);
}

Services.add("IDeprecatedGitRefService", { serviceFactory: GitRefService, options: ServiceOptions.Persistant });

interface IPaginatedGitRefRestClient {
    getPaginatedRefs(
        repositoryId: string,
        project?: string,
        filter?: string,
        filterContains?: string,
        top?: number,
        continuationToken?: string,
    ): Promise<PagedList<GitRef>>;
}

/**
 * Manual Rest client until we return continuationToken in GitRestClient (will change signature).
 */
export class PaginatedGitRefRestClient extends RestClientBase implements IPaginatedGitRefRestClient {
    public async getPaginatedRefs(
        repositoryId: string,
        project?: string,
        filter?: string,
        filterContains?: string,
        top?: number,
        continuationToken?: string
    ): Promise<PagedList<GitRef>> {
        const queryValues = {
            filter,
            filterContains,
            "$top": top,
            continuationToken,
        };

        return this.beginRequest<Response>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/refs/{*filter}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
            },
            queryParams: queryValues,
            returnRawResponse: true,
        }).then(async response => new PagedList<GitRef>(
            await response.text().then(deserializeVssJsonObject) as GitRef[],
            getContinuationToken(response)));
    }
}

function filterBranchesOnly(refs: GitRef[]): GitRef[] {
    return refs.filter(ref => isBranch(ref.name));
}

function getContinuationToken(response: Response): string | undefined {
    return response.headers.get("x-ms-continuationtoken") || undefined;
}

const tfsService = "00025394-6065-48ca-87d9-7f5672854ef7";
RestClients.add("IPaginatedGitRefRestClient", { 
    factory: PaginatedGitRefRestClient,
    options: {
        serviceInstanceType: tfsService,
    }
});
